import { AttendanceReport, ClassReport, BehaviorReport, TeacherAttendanceReport } from '../types';
import { attendanceService, studentService, classService, behaviorService, teacherAttendanceService } from './apiService';

export const reportService = {
  async generateStudentReport(studentId: string, startDate: string, endDate: string): Promise<AttendanceReport> {
    const [attendanceRecords, student, behaviorIncidents] = await Promise.all([
      attendanceService.getByStudent(studentId, startDate, endDate),
      studentService.getById(studentId),
      behaviorService.getByStudent(studentId, startDate, endDate)
    ]);

    if (!student) throw new Error('Student not found');

    const totalDays = attendanceRecords.length;
    const presentDays = attendanceRecords.filter(r => r.status === 'present').length;
    const absentDays = attendanceRecords.filter(r => r.status === 'absent').length;
    const attendanceRate = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;

    // Calculate streaks
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    // Sort records by date (newest first)
    const sortedRecords = attendanceRecords.sort((a, b) => b.date.localeCompare(a.date));
    
    for (const record of sortedRecords) {
      if (record.status === 'present') {
        tempStreak++;
        if (tempStreak === 1) currentStreak = tempStreak; // First present day
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 0;
      }
    }
    longestStreak = Math.max(longestStreak, tempStreak);


    return {
      studentId,
      studentName: `${student.firstName} ${student.lastName}`,
      totalDays,
      presentDays,
      absentDays,
      attendanceRate,
      currentStreak,
      longestStreak,
      behaviorIncidents: behaviorIncidents.length,
      flags: [] // Not implemented in local version
    };
  },

  async generateClassReport(classId: string, startDate: string, endDate: string): Promise<ClassReport> {
    const [students, classData] = await Promise.all([
      studentService.getByClass(classId),
      classService.getById(classId)
    ]);

    if (!classData) throw new Error('Class not found');

    const studentReports = await Promise.all(
      students.map(student => 
        reportService.generateStudentReport(student.id, startDate, endDate)
      )
    );

    const averageAttendance = studentReports.length > 0 
      ? studentReports.reduce((sum, report) => sum + report.attendanceRate, 0) / studentReports.length 
      : 0;
    
    const totalBehaviorIncidents = studentReports.reduce((sum, report) => sum + report.behaviorIncidents, 0);

    const topPerformers = studentReports
      .filter(report => report.attendanceRate >= 90 && report.behaviorIncidents === 0)
      .map(report => report.studentName);

    const needsAttention = studentReports
      .filter(report => report.attendanceRate < 70 || report.behaviorIncidents > 3)
      .map(report => report.studentName);

    return {
      classId,
      className: classData.name,
      totalStudents: students.length,
      averageAttendance,
      totalBehaviorIncidents,
      topPerformers,
      needsAttention,
      dateRange: { start: startDate, end: endDate }
    };
  },

  async generateBehaviorReport(classId: string, startDate: string, endDate: string): Promise<BehaviorReport> {
    try {
      const [students, classData, allIncidents] = await Promise.all([
        studentService.getByClass(classId),
        classService.getById(classId),
        behaviorService.getAll()
      ]);

      if (!classData) throw new Error('Class not found');
      if (!students || students.length === 0) throw new Error('No students found in class');

      console.log('Behavior report data:', {
        classId,
        className: classData.name,
        studentsCount: students.length,
        allIncidentsCount: allIncidents.length,
        startDate,
        endDate
      });

    // Filter incidents for this class and date range
    const classStudentIds = students.map(s => s.id);
    
    const incidents = allIncidents.filter(incident => {
      const incidentDate = new Date(incident.timestamp);
      const incidentDateStr = incidentDate.toISOString().split('T')[0];
      return classStudentIds.includes(incident.studentId) && 
             incidentDateStr >= startDate && 
             incidentDateStr <= endDate;
    });

    console.log('Filtered incidents:', {
      totalIncidents: incidents.length,
      classStudentIds,
      incidents: incidents.map(i => ({
        id: i.id,
        studentId: i.studentId,
        type: i.type,
        severity: i.severity,
        timestamp: i.timestamp,
        resolved: i.resolved
      }))
    });

    // Calculate statistics
    const totalIncidents = incidents.length;
    
    const incidentsByType = {
      'profane-language': incidents.filter(i => i.type === 'profane-language').length,
      'disruption': incidents.filter(i => i.type === 'disruption').length,
      'aggression': incidents.filter(i => i.type === 'aggression').length,
      'other': incidents.filter(i => i.type === 'other').length
    };

    const incidentsBySeverity = {
      low: incidents.filter(i => i.severity === 'low').length,
      medium: incidents.filter(i => i.severity === 'medium').length,
      high: incidents.filter(i => i.severity === 'high').length
    };

    const resolvedIncidents = incidents.filter(i => i.resolved).length;
    const unresolvedIncidents = incidents.filter(i => !i.resolved).length;

    // Get students with incidents
    const studentsWithIncidents = students
      .map(student => {
        const studentIncidents = incidents.filter(i => i.studentId === student.id);
        if (studentIncidents.length === 0) return null;
        
        const lastIncident = studentIncidents
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
        
        return {
          studentId: student.id,
          studentName: `${student.firstName} ${student.lastName}`,
          incidentCount: studentIncidents.length,
          lastIncidentDate: new Date(lastIncident.timestamp).toISOString().split('T')[0]
        };
      })
      .filter(Boolean) as Array<{
        studentId: string;
        studentName: string;
        incidentCount: number;
        lastIncidentDate: string;
      }>;

      return {
        classId,
        className: classData.name,
        totalIncidents,
        incidentsByType,
        incidentsBySeverity,
        resolvedIncidents,
        unresolvedIncidents,
        studentsWithIncidents,
        dateRange: { start: startDate, end: endDate }
      };
    } catch (error) {
      console.error('Error generating behavior report:', error);
      throw new Error(`Failed to generate behavior report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  async generateTeacherAttendanceReport(startDate: string, endDate: string): Promise<TeacherAttendanceReport> {
    try {
      console.log('Generating teacher attendance report for:', { startDate, endDate });
      
      const [teacherAttendanceRecords, classes] = await Promise.all([
        teacherAttendanceService.getAll(),
        classService.getAll()
      ]);

      // Filter records by date range
      const filteredRecords = teacherAttendanceRecords.filter(record => 
        record.date >= startDate && record.date <= endDate
      );

      console.log('Filtered teacher attendance records:', filteredRecords);

      const totalDays = filteredRecords.length;
      const presentDays = filteredRecords.filter(r => r.status === 'present').length;
      const absentDays = filteredRecords.filter(r => r.status === 'absent').length;
      const attendanceRate = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;

      // Group by class
      const classStats = classes.map(cls => {
        const classRecords = filteredRecords.filter(r => r.classId === cls.id);
        const classTotalDays = classRecords.length;
        const classPresentDays = classRecords.filter(r => r.status === 'present').length;
        const classAbsentDays = classRecords.filter(r => r.status === 'absent').length;
        const classAttendanceRate = classTotalDays > 0 ? (classPresentDays / classTotalDays) * 100 : 0;

        return {
          classId: cls.id,
          className: cls.name,
          totalDays: classTotalDays,
          presentDays: classPresentDays,
          absentDays: classAbsentDays,
          attendanceRate: classAttendanceRate
        };
      });

      console.log('Generated teacher attendance report:', {
        totalDays,
        presentDays,
        absentDays,
        attendanceRate,
        classes: classStats
      });

      return {
        totalDays,
        presentDays,
        absentDays,
        attendanceRate,
        classes: classStats,
        dateRange: { start: startDate, end: endDate }
      };
    } catch (error) {
      console.error('Error generating teacher attendance report:', error);
      throw new Error(`Failed to generate teacher attendance report: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};
